// components/ui/Select.jsx - Shadcn style Select
import React, { useState } from "react";
import { ChevronDown, Check, Search, X } from "@/icons";
import { cn } from "../../utils/cn";
import Button from "./Button";
import Input from "./Input";
import Sheet from "@/sot/primitives/Sheet.jsx";

const Select = React.forwardRef(({
    className,
    triggerClassName,
    valueClassName,
    iconClassName,
    indicatorSrc,
    indicatorAlt = "",
    leadingIcon,
    leadingIconMask = false,
    contentClassName,
    optionClassName,
    options = [],
    children,
    value,
    defaultValue,
    placeholder = "Select an option",
    searchPlaceholder = "Search options...",
    noResultsLabel = "No options found",
    noOptionsLabel = "No options available",
    multiple = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    searchable = false,
    clearable = false,
    loading = false,
    tone = "surface",
    id,
    name,
    onChange,
    onValueChange,
    onOpenChange,
    mobileSheet = false,
    mobileSheetTitle,
    ...props
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
    const [hostedByMobileSheet, setHostedByMobileSheet] = useState(false);
    const rootRef = React.useRef(null);
    const triggerRef = React.useRef(null);
    const instanceIdRef = React.useRef(`select-${Math.random()?.toString(36)?.substr(2, 9)}`);
    // Do not stack a picker Sheet on a parent Institution Sheet. A mobile
    // picker inside one stays bounded and scrollable inside that parent.
    const useMobileSheet = mobileSheet && isMobile && !hostedByMobileSheet;
    const useInlineMobileSheetPicker = mobileSheet && isMobile && hostedByMobileSheet;

    React.useEffect(() => {
        const media = window.matchMedia("(max-width: 767px)");
        const sync = () => setIsMobile(media.matches);
        sync();
        media.addEventListener("change", sync);
        return () => media.removeEventListener("change", sync);
    }, []);

    const resolvedOptions = React.useMemo(() => {
        if (Array.isArray(options) && options.length > 0) return options;

        return React.Children.toArray(children)
            .filter((child) => React.isValidElement(child))
            .map((child) => ({
                value: child.props?.value,
                label: child.props?.children,
                disabled: child.props?.disabled,
            }))
            .filter((option) => option?.value !== undefined);
    }, [children, options]);

    // Generate unique ID if not provided
    const selectId = id || instanceIdRef.current;

    React.useImperativeHandle(ref, () => triggerRef.current);

    const closeSelect = React.useCallback(() => {
        setIsOpen(false);
        setSearchTerm("");
        onOpenChange?.(false);
    }, [onOpenChange]);

    React.useEffect(() => {
        if (!isOpen || typeof document === "undefined") return undefined;

        const handlePointerDown = (event) => {
            if ((mobileSheet && isMobile) || !rootRef.current || rootRef.current.contains(event.target)) return;
            closeSelect();
        };

        const handleKeyDown = (event) => {
            if (event.key !== "Escape") return;
            closeSelect();
            triggerRef.current?.focus?.();
        };

        const handleSelectOpen = (event) => {
            if (event?.detail?.id && event.detail.id !== instanceIdRef.current) {
                closeSelect();
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("afrimed:select-open", handleSelectOpen);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("afrimed:select-open", handleSelectOpen);
        };
    }, [closeSelect, isOpen, mobileSheet, isMobile]);

    // Filter options based on search
    const filteredOptions = searchable && searchTerm
        ? resolvedOptions?.filter(option =>
            option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
            (option?.value && option?.value?.toString()?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
        )
        : resolvedOptions;

    // Get selected option(s) for display
    const getSelectedDisplay = () => {
        if (!value) return placeholder;

        if (multiple) {
            const selectedOptions = resolvedOptions?.filter(opt => value?.includes(opt?.value));
            if (selectedOptions?.length === 0) return placeholder;
            if (selectedOptions?.length === 1) return selectedOptions?.[0]?.label;
            return `${selectedOptions?.length} items selected`;
        }

        const selectedOption = resolvedOptions?.find(opt => opt?.value === value);
        return selectedOption ? selectedOption?.label : placeholder;
    };

    const handleToggle = () => {
        if (!disabled) {
            const newIsOpen = !isOpen;
            setHostedByMobileSheet(Boolean(rootRef.current?.closest?.('[data-bottom-sheet="open"]')));
            setIsOpen(newIsOpen);
            onOpenChange?.(newIsOpen);
            if (newIsOpen && typeof window !== "undefined") {
                window.dispatchEvent(
                    new CustomEvent("afrimed:select-open", {
                        detail: { id: instanceIdRef.current },
                    }),
                );
            }
            if (!newIsOpen) {
                setSearchTerm("");
            }
        }
    };

    const handleOptionSelect = (option, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        if (multiple) {
            const newValue = value || [];
            const updatedValue = newValue?.includes(option?.value)
                ? newValue?.filter(v => v !== option?.value)
                : [...newValue, option?.value];
            // Create synthetic event object for compatibility
            onChange?.({ 
                target: { 
                    name, 
                    value: updatedValue, 
                    type: 'select-multiple' 
                } 
            });
            onValueChange?.(updatedValue);
        } else {
            closeSelect();
            // Create synthetic event object for compatibility
            onChange?.({ 
                target: { 
                    name, 
                    value: option?.value, 
                    type: 'select-one' 
                } 
            });
            onValueChange?.(option?.value);
            if (typeof window !== "undefined") {
                window.setTimeout(closeSelect, 0);
            }
        }
    };

    const handleClear = (e) => {
        e?.stopPropagation();
        // Create synthetic event object for compatibility
        onChange?.({ 
            target: { 
                name, 
                value: multiple ? [] : '', 
                type: multiple ? 'select-multiple' : 'select-one' 
            } 
        });
        onValueChange?.(multiple ? [] : "");
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e?.target?.value);
    };

    const isSelected = (optionValue) => {
        if (multiple) {
            return value?.includes(optionValue) || false;
        }
        return value === optionValue;
    };

    const hasValue = multiple ? value?.length > 0 : value !== undefined && value !== '';

    return (
        <div ref={rootRef} className={cn("relative", className)}>
            {label && (
                <label
                    htmlFor={selectId}
                    className={cn(
                        "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block",
                        error ? "text-destructive" : "text-foreground"
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </label>
            )}
            <div className="relative">
                <button
                    ref={triggerRef}
                    id={selectId}
                    type="button"
                    className={cn(
                        "flex h-12 w-full items-center justify-between rounded-[12px] border px-4 py-3 pr-5 text-body-medium text-on-surface ring-offset-background transition focus:outline-none focus:ring-2 focus:ring-[rgba(29,78,216,0.14)] focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
                        tone === "ghost"
                          ? "border-[rgba(var(--clr-outline-variant),0.9)] bg-[rgb(var(--clr-surface-container-low))] shadow-none"
                          : "border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] shadow-[var(--elev-1)]",
                        error && "border-destructive focus:ring-destructive",
                        !hasValue && "text-on-surface-var",
                        triggerClassName
                    )}
                    onClick={handleToggle}
                    disabled={disabled}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    {...props}
                >
                    <span className={cn("flex min-w-0 items-center gap-2 truncate", valueClassName)}>
                        {leadingIcon ? (
                            leadingIconMask ? (
                                <span
                                    aria-hidden="true"
                                    className="h-[18px] w-[18px] shrink-0"
                                    style={{
                                        backgroundColor: "rgb(var(--clr-on-surface-variant))",
                                        WebkitMaskImage: `url("${leadingIcon}")`,
                                        maskImage: `url("${leadingIcon}")`,
                                        WebkitMaskRepeat: "no-repeat",
                                        maskRepeat: "no-repeat",
                                        WebkitMaskPosition: "center",
                                        maskPosition: "center",
                                        WebkitMaskSize: "contain",
                                        maskSize: "contain",
                                    }}
                                />
                            ) : (
                                <img src={leadingIcon} alt="" aria-hidden="true" className="h-[18px] w-[18px] shrink-0 object-contain" />
                            )
                        ) : null}
                        <span className="truncate">{getSelectedDisplay()}</span>
                    </span>

                    <div className="flex shrink-0 items-center gap-2 pl-3">
                        {loading && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}

                        {clearable && hasValue && !loading && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-4 w-4"
                                onClick={handleClear}
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        )}

                        {indicatorSrc ? (
                            <img
                                src={indicatorSrc}
                                alt={indicatorAlt}
                                className={cn("h-4 w-4 text-on-surface-var transition-transform", isOpen && "rotate-180", iconClassName)}
                            />
                        ) : (
                            <ChevronDown className={cn("h-4 w-4 text-on-surface-var transition-transform", isOpen && "rotate-180", iconClassName)} />
                        )}
                    </div>
                </button>

                {/* Hidden native select for form submission */}
                <select
                    name={name}
                    value={value || ''}
                    onChange={() => { }} // Controlled by our custom logic
                    className="sr-only"
                    tabIndex={-1}
                    multiple={multiple}
                    required={required}
                >
                    <option value="">{placeholder}</option>
                    {resolvedOptions?.map(option => (
                        <option key={option?.value} value={option?.value}>
                            {option?.label}
                        </option>
                    ))}
                </select>

                {/* Dropdown */}
                {isOpen && !useMobileSheet && (
                    <div
                        role="listbox"
                        aria-multiselectable={multiple || undefined}
                        className={cn("absolute z-[80] mt-2 w-full overflow-hidden rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.7)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-4)]", useInlineMobileSheetPicker && "select-mobile-sheet__inline", contentClassName)}
                    >
                        {searchable && (
                            <div className="border-b border-[rgba(var(--clr-outline-variant),0.55)] p-2">
                                <div className="relative">
                                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        className="pl-8"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="py-1 max-h-60 overflow-auto">
                            {filteredOptions?.length === 0 ? (
                                <div className="px-4 py-3 text-body-small text-on-surface-var">
                                    {searchTerm ? noResultsLabel : noOptionsLabel}
                                </div>
                            ) : (
                                filteredOptions?.map((option) => (
                                    <div
                                        key={option?.value}
                                        role="option"
                                        aria-selected={isSelected(option?.value)}
                                        className={cn(
                                            "relative flex cursor-pointer select-none items-center px-4 py-3 text-body-medium text-on-surface outline-none transition hover:bg-[rgba(var(--clr-on-surface),0.04)]",
                                            isSelected(option?.value) && "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))]",
                                            option?.disabled && "pointer-events-none opacity-50",
                                            optionClassName
                                        )}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={(event) => !option?.disabled && handleOptionSelect(option, event)}
                                    >
                                        <span className="flex-1">{option?.label}</span>
                                        {multiple && isSelected(option?.value) && (
                                            <Check className="h-4 w-4 text-[rgb(var(--afrimed-primary))]" />
                                        )}
                                        {option?.description && (
                                            <span className="ml-2 text-xs text-on-surface-var">
                                                {option?.description}
                                            </span>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
                {isOpen && useMobileSheet && (
                    <Sheet open onClose={closeSelect} ariaLabel={mobileSheetTitle || label || "Options"}>
                        <div className="select-mobile-sheet">
                            <div className="select-mobile-sheet__header"><h2>{mobileSheetTitle || label}</h2><button type="button" onClick={closeSelect} aria-label={`Close ${mobileSheetTitle || label}`}>×</button></div>
                            {searchable && (
                                <div className="border-b border-[rgba(var(--clr-outline-variant),0.55)] p-2">
                                    <div className="relative"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" /><Input autoFocus placeholder={searchPlaceholder} value={searchTerm} onChange={handleSearchChange} className="pl-8" /></div>
                                </div>
                            )}
                            <div role="listbox" aria-multiselectable={multiple || undefined} className="select-mobile-sheet__options">
                                {filteredOptions?.length === 0 ? <div className="px-4 py-3 text-body-small text-on-surface-var">{searchTerm ? noResultsLabel : noOptionsLabel}</div> : filteredOptions?.map((option) => <button key={option?.value} type="button" role="option" aria-selected={isSelected(option?.value)} className={cn("relative flex w-full items-center px-4 py-3 text-left text-body-medium text-on-surface", isSelected(option?.value) && "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))]")} onClick={(event) => !option?.disabled && handleOptionSelect(option, event)}><span className="flex-1">{option?.label}</span>{multiple && isSelected(option?.value) ? <Check className="h-4 w-4 text-[rgb(var(--afrimed-primary))]" /> : null}</button>)}
                            </div>
                        </div>
                    </Sheet>
                )}
            </div>
            {description && !error && (
                <p className="text-sm text-muted-foreground mt-1">
                    {description}
                </p>
            )}
            {error && (
                <p className="text-sm text-destructive mt-1">
                    {error}
                </p>
            )}
        </div>
    );
});

Select.displayName = "Select";

export default Select;
